import { KeyboardEvent, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Code2, Film, FileText, Sparkles, Workflow } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import {
  PortfolioCategoryId,
  PortfolioProject,
  isPortfolioCategory,
  portfolioCategories,
  portfolioProjects,
} from "@/data/portfolioData";

const categoryIcons = {
  web: Code2,
  ugc: Sparkles,
  automation: Workflow,
  video: Film,
} as const;

const gradient = "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))";
const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";
const ONBOARDING_FORM_URL = "https://xenticsolutions.com/Website_dev_onboarding_form/";

const ProjectCard = ({ project, priority }: { project: PortfolioProject; priority: boolean }) => (
  <article className="glass-card glass-card-hover rounded-2xl overflow-hidden flex flex-col h-full">
    {project.image ? (
      <img
        src={project.image}
        alt={project.imageAlt ?? project.title}
        width={1200}
        height={750}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className="w-full aspect-[16/10] object-cover object-top border-b border-border"
      />
    ) : (
      <div
        className="w-full aspect-[16/10] flex flex-col items-center justify-center gap-2 border-b border-border px-6 text-center"
        style={{ background: "linear-gradient(135deg, hsl(189 100% 50% / 0.12), hsl(243 76% 59% / 0.2))" }}
        role="img"
        aria-label={`${project.title} logo tile`}
      >
        <span className="font-heading text-2xl sm:text-3xl font-bold text-gradient break-words max-w-full">
          {project.title}
        </span>
        {project.note && <span className="text-xs uppercase tracking-widest text-muted-foreground">{project.note}</span>}
      </div>
    )}

    <div className="p-5 md:p-6 flex flex-col flex-1">
      {project.subtype && (
        <p className="text-xs font-medium uppercase tracking-widest text-primary mb-2">{project.subtype}</p>
      )}
      <h3 className="font-heading text-xl font-bold mb-2 break-words">{project.title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{project.description}</p>
      {project.altLink && (
        <p className="mt-2 text-xs text-muted-foreground">
          Also live at{" "}
          <a
            href={project.altLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-primary hover:underline cursor-pointer rounded ${focusRing}`}
          >
            {project.altLink.label}
          </a>
        </p>
      )}

      <div className="mt-auto pt-5 flex flex-wrap items-center gap-3">
        <a
          href={project.liveUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${project.liveLabel}: ${project.title} (opens in a new tab)`}
          className={`inline-flex items-center justify-center gap-2 min-h-11 px-5 rounded-xl text-sm font-semibold text-white cursor-pointer hover:-translate-y-0.5 transition-transform duration-300 ${focusRing}`}
          style={{ background: gradient }}
        >
          {project.liveLabel} <ArrowUpRight className="h-4 w-4" />
        </a>
        {project.caseStudyUrl && (
          <a
            href={project.caseStudyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center min-h-11 px-3 text-sm font-medium text-primary hover:underline cursor-pointer rounded-lg ${focusRing}`}
          >
            View Case Study
          </a>
        )}
        {project.videoUrl && (
          <a
            href={project.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center min-h-11 px-3 text-sm font-medium text-primary hover:underline cursor-pointer rounded-lg ${focusRing}`}
          >
            Watch Video
          </a>
        )}
      </div>
    </div>
  </article>
);

const Portfolio = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get("category");
  const active: PortfolioCategoryId = isPortfolioCategory(param) ? param : "web";
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const category = portfolioCategories.find((c) => c.id === active)!;
  const projects = portfolioProjects.filter((p) => p.category === active);

  const select = (id: PortfolioCategoryId) => setSearchParams({ category: id }, { replace: true });

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = portfolioCategories.length - 1;
    let next = index;
    if (e.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (e.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    else return;
    e.preventDefault();
    const id = portfolioCategories[next].id;
    select(id);
    tabRefs.current[id]?.focus();
  };

  return (
    <Layout>
      <SEOHead
        title="Portfolio | Web Development, AI UGC, AI Automation, Video Editing | Xentic Solutions"
        description="Explore Xentic Solutions work across web development, AI UGC, AI automation and video editing."
        path="/portfolio"
      />

      <div className="relative overflow-hidden">
      <div className="absolute top-16 -left-32 w-96 h-96 bg-primary/15 rounded-full blur-[120px] animate-blob" />
      <section className="pt-16 pb-8 md:pt-24 md:pb-10 relative">
        <div className="container text-center max-w-3xl relative z-10">
          <ScrollReveal>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Portfolio</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold">
              Our <span className="text-gradient">Work</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground">
              Websites, AI UGC, automation and video editing from the Xentic team.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="pb-20 md:pb-28 relative">
        <div className="container">
          <div
            role="tablist"
            aria-label="Portfolio categories"
            className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 max-w-4xl mx-auto"
          >
            {portfolioCategories.map((c, i) => {
              const Icon = categoryIcons[c.id];
              const selected = c.id === active;
              return (
                <button
                  key={c.id}
                  ref={(el) => (tabRefs.current[c.id] = el)}
                  role="tab"
                  id={`tab-${c.id}`}
                  aria-selected={selected}
                  aria-controls={`panel-${c.id}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => select(c.id)}
                  onKeyDown={(e) => onKeyDown(e, i)}
                  className={`flex items-center justify-center gap-2 min-h-12 px-3 py-2 rounded-xl border text-sm font-medium text-center cursor-pointer transition-all duration-300 ${focusRing} ${
                    selected
                      ? "text-white border-transparent"
                      : "text-muted-foreground border-border hover:text-foreground hover:border-primary/40 bg-secondary/30"
                  }`}
                  style={selected ? { background: gradient } : undefined}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{c.label}</span>
                </button>
              );
            })}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-5 max-w-xl mx-auto">{category.blurb}</p>

          <div
            role="tabpanel"
            id={`panel-${active}`}
            aria-labelledby={`tab-${active}`}
            tabIndex={0}
            className={`mt-10 rounded-2xl ${focusRing}`}
          >
            {projects.length > 0 ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
                {projects.map((p, i) => (
                  <ProjectCard key={p.id} project={p} priority={i < 3} />
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-2xl p-8 md:p-12 text-center max-w-2xl mx-auto">
                <h2 className="font-heading text-2xl font-bold mb-3">{category.emptyTitle}</h2>
                <p className="text-muted-foreground mb-6">{category.emptyText}</p>
                <Link
                  to="/contact"
                  className={`inline-flex items-center justify-center gap-2 min-h-11 px-6 rounded-xl text-sm font-semibold text-white cursor-pointer hover:-translate-y-0.5 transition-transform duration-300 ${focusRing}`}
                  style={{ background: gradient }}
                >
                  Book Free Consultation <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            {active === "web" && (
              <ScrollReveal>
                <a
                  href={ONBOARDING_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5 md:gap-6 rounded-2xl border border-dashed border-primary/30 bg-secondary/20 p-6 md:p-8 hover:border-primary/60 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer ${focusRing}`}
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: gradient }}
                  >
                    <FileText className="h-7 w-7 text-white" aria-hidden="true" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-xl md:text-2xl mb-1">Want a Website Like These?</h3>
                    <p className="text-sm text-muted-foreground">
                      Fill our Website Planning Questionnaire and our team will start planning your project.
                    </p>
                  </div>
                  <span
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shrink-0 group-hover:gap-3 transition-all"
                    style={{ background: gradient }}
                  >
                    Start Questionnaire <ArrowRight className="h-4 w-4" />
                  </span>
                </a>
              </ScrollReveal>
            )}
          </div>
        </div>
      </section>
      </div>
    </Layout>
  );
};

export default Portfolio;
